/* ==========================================================================
   animation.js — FULL REWRITE (Clarity)
   Goals:
   1) No scroll-zoom on the HOMEPAGE (mobile ≤768px).
   2) Leave other pages/desktop behavior intact.
   3) Extra hardening: forcibly neutralize any transforms/transitions
      inside the AI-generated slideshow on the homepage (mobile).
   ========================================================================== */

const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

/* ---------------------------
   Utilities
---------------------------- */
function isHome() {
  return !!(document.body && document.body.classList.contains('template-index'));
}

function isMobile() {
  return window.matchMedia('(max-width: 768px)').matches;
}

/* ---------------------------
   Reveal-on-scroll
---------------------------- */
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const el = element.target;
      if (el.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (el.hasAttribute('data-cascade')) el.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(el);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  // Keep reveal behavior everywhere EXCEPT homepage on mobile
  if (isHome() && isMobile()) return;

  const els = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (!els.length) return;

  if (isDesignModeEvent) {
    els.forEach((el) => el.classList.add('scroll-trigger--design-mode'));
    return;
  }

  const observer = new IntersectionObserver(onIntersection, { rootMargin: '0px 0px -50px 0px' });
  els.forEach((el) => observer.observe(el));
}

/* ---------------------------
   Scroll zoom
---------------------------- */
function initializeScrollZoomAnimationTrigger() {
  // HARD-OFF: homepage on mobile has no zoom
  if (isHome() && isMobile()) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const els = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (!els.length) return;

  const scaleAmount = 0.2 / 100;

  els.forEach((element) => {
    let elementIsVisible = false;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;
        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) return 0;      // not reached yet
  if (elementPositionY + elementHeight < scrollY) return 100;     // completely past

  const distance = scrollY + viewportHeight - elementPositionY;
  const percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

/* ---------------------------
   HOMEPAGE (mobile) anti-zoom HARDENER for AI slideshow
   - Enforces transform: none !important and kills transitions/animations
   - Works even if some 3rd-party code tries to set inline styles later
---------------------------- */
function applyHomepageMobileNoZoomHardener() {
  if (!(isHome() && isMobile())) return;

  // 1) Inject a scoped CSS guard (very cheap, future-proof)
  const STYLE_ID = 'home-mobile-no-zoom-guard';
  let style = document.getElementById(STYLE_ID);
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
@media (max-width: 768px){
  body.template-index [class^="ai-image-slideshow-"] img,
  body.template-index [class*=" ai-image-slideshow-"] img {
    transform: none !important;
    scale: 1 !important;
    animation: none !important;
    transition: none !important;
    will-change: auto !important;
  }
  body.template-index [class^="ai-image-slideshow-"] *,
  body.template-index [class*=" ai-image-slideshow-"] * {
    --zoom-in-ratio: 1 !important;
  }
}
    `.trim();
    document.head.appendChild(style);
  } else {
    // ensure it sits last for max precedence
    document.head.appendChild(style);
  }

  // 2) Inline reset for anything already in the DOM
  const containers = document.querySelectorAll(
    '[class^="ai-image-slideshow-"], [class*=" ai-image-slideshow-"]'
  );
  containers.forEach((wrap) => {
    wrap.classList.add('no-zoom-enforced');
    const imgs = wrap.querySelectorAll('img');
    imgs.forEach((img) => {
      try {
        img.style.setProperty('transform', 'none', 'important');
        img.style.setProperty('animation', 'none', 'important');
        img.style.setProperty('transition', 'none', 'important');
        img.style.setProperty('will-change', 'auto', 'important');
      } catch (_) {}
    });
  });

  // 3) MutationObserver: if any script tries to re-apply transforms,
  //    immediately neutralize them.
  const root = document.body || document.documentElement;
  try {
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === 'attributes' && m.target && m.target.matches && m.target.matches('img')) {
          const img = m.target;
          if (img.closest('[class^="ai-image-slideshow-"], [class*=" ai-image-slideshow-"]')) {
            const st = img.style && img.style.transform;
            if (st && st !== 'none') {
              img.style.setProperty('transform', 'none', 'important');
            }
          }
        } else if (m.addedNodes && m.addedNodes.length) {
          m.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            const imgs = node.querySelectorAll
              ? node.querySelectorAll('[class^="ai-image-slideshow-"] img, [class*=" ai-image-slideshow-"] img')
              : [];
            imgs.forEach((img) => {
              try {
                img.style.setProperty('transform', 'none', 'important');
                img.style.setProperty('animation', 'none', 'important');
                img.style.setProperty('transition', 'none', 'important');
                img.style.setProperty('will-change', 'auto', 'important');
              } catch (_) {}
            });
          });
        }
      }
    });
    mo.observe(root, { attributes: true, attributeFilter: ['style', 'class'], childList: true, subtree: true });
  } catch (_) {
    // no-op
  }
}

/* ---------------------------
   Boot
---------------------------- */
window.addEventListener('DOMContentLoaded', () => {
  // Apply the AI-slideshow anti-zoom first so nothing flashes
  applyHomepageMobileNoZoomHardener();

  // Then run the theme’s animations (with our guards)
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
